import { CrewCheckIn } from '../types'

const CREW_STORAGE_KEY_PREFIX = 'crew_checkins_'
const URL_FRAGMENT_KEY = 'checkins'

export class CrewStorage {
  static getCheckIns(slug: string): CrewCheckIn[] {
    try {
      const key = CREW_STORAGE_KEY_PREFIX + slug
      const stored = localStorage.getItem(key)
      if (!stored) return []
      
      const parsed = JSON.parse(stored) as CrewCheckIn[]
      // Convert timestamp strings back to Date objects
      return parsed.map(checkIn => ({
        ...checkIn,
        timestamp: new Date(checkIn.timestamp)
      }))
    } catch (error) {
      console.error('Error loading crew check-ins:', error)
      return []
    }
  }

  static saveCheckIns(slug: string, checkIns: CrewCheckIn[]): void {
    const key = CREW_STORAGE_KEY_PREFIX + slug
    try {
      localStorage.setItem(key, JSON.stringify(checkIns))
      // Dispatch custom event to notify other components in the same tab
      window.dispatchEvent(new CustomEvent(`crew_checkins_updated_${slug}`))
    } catch (error) {
      console.error('Error saving crew check-ins:', error)
      // Handle LocalStorage quota exceeded
      if (error instanceof DOMException && error.code === 22) {
        console.warn('LocalStorage quota exceeded, clearing old data')
        this.clearOldCheckIns()
        // Try again after clearing
        try {
          localStorage.setItem(key, JSON.stringify(checkIns))
          // Dispatch event after successful retry
          window.dispatchEvent(new CustomEvent(`crew_checkins_updated_${slug}`))
        } catch (retryError) {
          console.error('Failed to save check-ins after clearing:', retryError)
        }
      }
    }
  }

  static addCheckIn(slug: string, checkIn: CrewCheckIn): void {
    const existing = this.getCheckIns(slug)
    
    // Remove any existing check-in for the same aid station
    const filtered = existing.filter(c => c.aidStationId !== checkIn.aidStationId)
    
    // Add the new check-in
    const updated = [...filtered, checkIn].sort((a, b) => 
      a.actualTimeMinutes - b.actualTimeMinutes
    )
    
    this.saveCheckIns(slug, updated)
  }

  static removeCheckIn(slug: string, aidStationId: string): void {
    const existing = this.getCheckIns(slug)
    const filtered = existing.filter(c => c.aidStationId !== aidStationId)
    this.saveCheckIns(slug, filtered)
  }

  static clearCheckIns(slug: string): void {
    const key = CREW_STORAGE_KEY_PREFIX + slug
    localStorage.removeItem(key)
    // Dispatch custom event to notify other components in the same tab
    window.dispatchEvent(new CustomEvent(`crew_checkins_updated_${slug}`))
  }

  static clearOldCheckIns(): void {
    // Clear check-ins older than 30 days
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - 30)

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith(CREW_STORAGE_KEY_PREFIX)) {
        try {
          const stored = localStorage.getItem(key)
          if (stored) {
            const checkIns = JSON.parse(stored) as CrewCheckIn[]
            const hasRecentCheckIns = checkIns.some(
              c => new Date(c.timestamp) > cutoffDate
            )
            if (!hasRecentCheckIns) {
              localStorage.removeItem(key)
            }
          }
        } catch (error) {
          // Remove corrupted entries
          localStorage.removeItem(key)
        }
      }
    }
  }

  // URL fragment sharing methods
  static encodeCheckInsToFragment(checkIns: CrewCheckIn[]): string {
    try {
      const simplified = checkIns.map(c => ({
        a: c.aidStationId,
        t: c.actualTimeMinutes,
        m: c.method,
        ts: c.timestamp.getTime(),
        n: c.notes
      }))
      const json = JSON.stringify(simplified)
      return btoa(json)
    } catch (error) {
      console.error('Error encoding check-ins:', error)
      return ''
    }
  }

  static decodeCheckInsFromFragment(encoded: string): CrewCheckIn[] {
    try {
      const json = atob(encoded)
      const simplified = JSON.parse(json)
      return simplified.map((c: any) => ({
        aidStationId: c.a,
        actualTimeMinutes: c.t,
        method: c.m,
        timestamp: new Date(c.ts),
        notes: c.n
      }))
    } catch (error) {
      console.error('Error decoding check-ins:', error)
      return []
    }
  }

  static getShareableUrl(slug: string): string {
    const checkIns = this.getCheckIns(slug)
    if (checkIns.length === 0) {
      return `${window.location.origin}/shared/${slug}?view=crew`
    }
    
    const encoded = this.encodeCheckInsToFragment(checkIns)
    return `${window.location.origin}/shared/${slug}?view=crew#${URL_FRAGMENT_KEY}=${encoded}`
  }

  static importFromUrl(slug: string, url: string): boolean {
    try {
      const urlObj = new URL(url)
      const fragment = urlObj.hash.slice(1) // Remove the #
      const params = new URLSearchParams(fragment)
      const encoded = params.get(URL_FRAGMENT_KEY)
      
      if (!encoded) return false
      
      const importedCheckIns = this.decodeCheckInsFromFragment(encoded)
      if (importedCheckIns.length === 0) return false
      
      // Merge with existing check-ins
      this.mergeCheckIns(slug, importedCheckIns)
      return true
    } catch (error) {
      console.error('Error importing check-ins from URL:', error)
      return false
    }
  }

  static loadFromCurrentUrl(slug: string): boolean {
    try {
      const fragment = window.location.hash.slice(1)
      if (!fragment) return false
      
      const params = new URLSearchParams(fragment)
      const encoded = params.get(URL_FRAGMENT_KEY)
      
      if (!encoded) return false
      
      const importedCheckIns = this.decodeCheckInsFromFragment(encoded)
      if (importedCheckIns.length === 0) return false
      
      // Merge with existing check-ins
      this.mergeCheckIns(slug, importedCheckIns)
      
      // Clean up URL fragment after importing
      if (window.history.replaceState) {
        const cleanUrl = window.location.pathname + window.location.search
        window.history.replaceState({}, '', cleanUrl)
      }
      
      return true
    } catch (error) {
      console.error('Error loading check-ins from URL:', error)
      return false
    }
  }

  private static mergeCheckIns(slug: string, importedCheckIns: CrewCheckIn[]): void {
    const existing = this.getCheckIns(slug)
    const merged = [...existing]
    
    // Merge imported check-ins, keeping the most recent for each aid station
    importedCheckIns.forEach(imported => {
      const existingIndex = merged.findIndex(c => c.aidStationId === imported.aidStationId)
      
      if (existingIndex === -1) {
        // New check-in, add it
        merged.push(imported)
      } else {
        // Existing check-in, keep the more recent one
        const existing = merged[existingIndex]
        if (imported.timestamp > existing.timestamp) {
          merged[existingIndex] = imported
        }
      }
    })
    
    this.saveCheckIns(slug, merged)
  }

  // Validation helpers
  static validateCheckIn(checkIn: Partial<CrewCheckIn>): string | null {
    if (!checkIn.aidStationId) {
      return 'Aid station ID is required'
    }
    
    if (typeof checkIn.actualTimeMinutes !== 'number' || checkIn.actualTimeMinutes < 0) {
      return 'Invalid time'
    }
    
    if (!['now', 'clock', 'elapsed'].includes(checkIn.method || '')) {
      return 'Invalid check-in method'
    }
    
    // Don't allow future times (with 5 minute tolerance for clock sync)
    const now = new Date()
    const checkInTime = new Date(checkIn.timestamp || now)
    if (checkInTime > new Date(now.getTime() + 5 * 60 * 1000)) {
      return 'Check-in time cannot be in the future'
    }
    
    return null
  }
}